import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Download, RefreshCw, Image as ImageIcon, Wand2, ClipboardCopy, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Social-Image-Generator
 * Nano-Banana-Bilder im Skandi-Brand-Look für LinkedIn-Posts.
 * 12 Presets aus dem 30-Tage-Plan + freier Prompt.
 */
export default function AdminSocialImageGenerator() {
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(null);          // preset_id oder 'custom'
  const [results, setResults] = useState({});      // {preset_id: {url, generated_at}}
  const [customPrompt, setCustomPrompt] = useState("");
  const [openPostId, setOpenPostId] = useState(null);

  const copyPost = async (text, title) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Post-Text "${title}" kopiert · ${text.length} Zeichen`);
    } catch {
      toast.error("Konnte Post nicht kopieren – bitte manuell markieren.");
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/social-image/presets`, { withCredentials: true })
      .then((r) => setPresets(r.data || []))
      .catch(() => toast.error("Presets konnten nicht geladen werden."));
  }, []);

  const generate = async (preset) => {
    setBusy(preset?.id || "custom");
    try {
      const payload = preset
        ? { preset_id: preset.id, prompt: preset.prompt }
        : { prompt: customPrompt };
      const r = await axios.post(`${API}/admin/social-image/generate`, payload, {
        withCredentials: true, timeout: 120000,
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setResults((prev) => ({
        ...prev,
        [preset?.id || "custom"]: { url, image_id: r.data.image_id, generated_at: r.data.created_at },
      }));
      toast.success(preset ? `${preset.title} · Bild erzeugt` : "Bild erzeugt");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bild-Generierung fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const download = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  };

  // Gruppierung pro Woche
  const weeks = [1, 2, 3, 4];

  return (
    <div className="space-y-10" data-testid="social-image-generator">
      {/* HEADER */}
      <header className="space-y-2">
        <div className="text-[11.5px] uppercase tracking-[0.28em] font-medium text-sand-500">
          Admin · Social-Media
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-sand-800 leading-[1.05]">
          LinkedIn-Bild-Generator
        </h1>
        <p className="text-sand-600 max-w-2xl">
          12 vorbereitete Bilder für deinen 30-Tage-LinkedIn-Plan. Klick auf
          &bdquo;Bild generieren&ldquo; – Nano Banana liefert in ~10-30 Sek. ein Skandi-Editorial-Bild
          im Brand-Look. Ca. 0,03 € pro Bild.
        </p>
      </header>

      {/* CUSTOM PROMPT */}
      <section className="bg-peach-50 border border-peach-100 rounded-2xl p-6">
        <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.24em] text-peach-dark font-semibold mb-3">
          <Wand2 className="h-4 w-4"/> Eigener Prompt
        </div>
        <Textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="z. B. Close-up of a steaming coffee cup on a hospital nightstand with morning light…"
          className="bg-white border-cream-300 rounded-xl min-h-[80px]"
          data-testid="custom-prompt-input"
        />
        <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
          <p className="text-xs text-sand-600 max-w-xl">
            Brand-Filter (Skandi · cream · peach · terra) wird automatisch vorangestellt.
          </p>
          <Button
            onClick={() => generate(null)}
            disabled={busy === "custom" || customPrompt.trim().length < 10}
            className="rounded-full bg-terra hover:bg-terra-dark text-white"
            data-testid="custom-generate-btn"
          >
            {busy === "custom"
              ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin"/> Generiere…</>
              : <><Sparkles className="h-4 w-4 mr-2"/> Bild generieren</>}
          </Button>
        </div>
        {results.custom && (
          <ResultPreview
            result={results.custom}
            label="Eigener Prompt"
            onDownload={() => download(results.custom.url, `social-custom-${Date.now()}.jpg`)}
          />
        )}
      </section>

      {/* 30-TAGE-PLAN · pro Woche gruppiert */}
      {weeks.map((wk) => {
        const wkPresets = presets.filter((p) => p.week === wk);
        if (wkPresets.length === 0) return null;
        return (
          <section key={wk} data-testid={`week-${wk}`}>
            <h2 className="font-heading font-semibold text-2xl text-sand-800 mb-1">
              Woche {wk}
            </h2>
            <p className="text-sand-600 text-sm mb-5">3 Posts · Mo / Mi / Fr</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {wkPresets.map((p) => {
                const isBusy = busy === p.id;
                const result = results[p.id];
                return (
                  <div
                    key={p.id}
                    className="bg-cream-100 border border-cream-300 rounded-2xl p-5 flex flex-col shadow-soft"
                    data-testid={`preset-${p.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-peach-dark">
                        {p.day} · {p.theme}
                      </span>
                      <span className="text-[10px] text-sand-500 font-medium uppercase tracking-wider">
                        W{p.week}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-sand-800 text-lg leading-snug mb-2">
                      {p.title}
                    </h3>
                    <p className="text-sand-600 text-sm leading-relaxed mb-3 line-clamp-3">
                      {p.hook}
                    </p>

                    {/* Post-Text Vorschau (zusammenklappbar) */}
                    {p.full_post && (
                      <div className="mb-4">
                        <button
                          onClick={() => setOpenPostId(openPostId === p.id ? null : p.id)}
                          className="text-xs text-sand-700 hover:text-terra inline-flex items-center gap-1 font-medium"
                          data-testid={`toggle-post-${p.id}`}
                        >
                          <Eye className="h-3.5 w-3.5"/>
                          {openPostId === p.id ? "Verbergen" : "Kompletten Post anzeigen"}
                          <span className="text-sand-400">· {p.char_count} Zeichen</span>
                        </button>
                        {openPostId === p.id && (
                          <pre className="mt-2 text-[12.5px] text-sand-700 bg-cream-50 border border-cream-300 rounded-xl p-3 whitespace-pre-wrap font-body max-h-72 overflow-y-auto leading-relaxed">
                            {p.full_post}
                          </pre>
                        )}
                      </div>
                    )}

                    {/* Action-Row */}
                    <div className="mt-auto space-y-2">
                      {p.full_post && (
                        <Button
                          variant="outline"
                          onClick={() => copyPost(p.full_post, p.title)}
                          className="rounded-full border-cream-300 hover:bg-cream-200 text-sand-700 w-full"
                          data-testid={`copy-post-${p.id}`}
                        >
                          <ClipboardCopy className="h-4 w-4 mr-2"/> Post-Text kopieren
                        </Button>
                      )}

                      {/* Bild-Slot */}
                      {result ? (
                        <ResultPreview
                          result={result}
                          compact
                          onDownload={() => download(result.url, `social-${p.id}.jpg`)}
                          onRegenerate={() => generate(p)}
                          regenBusy={isBusy}
                        />
                      ) : (
                        <Button
                          onClick={() => generate(p)}
                          disabled={isBusy}
                          className="rounded-full bg-terra hover:bg-terra-dark text-white w-full"
                          data-testid={`generate-${p.id}`}
                        >
                          {isBusy
                            ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin"/> Generiere…</>
                            : <><Sparkles className="h-4 w-4 mr-2"/> Bild generieren</>}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* HINT */}
      <div className="bg-cream-100 border border-cream-300 rounded-2xl p-5 flex items-start gap-3">
        <ImageIcon className="h-5 w-5 text-sand-500 mt-0.5 shrink-0" strokeWidth={1.5}/>
        <p className="text-sm text-sand-600 leading-relaxed">
          Generierte Bilder sind dauerhaft auf dem Server gespeichert und nur für Admins zugänglich.
          Du kannst sie als JPG / PNG runterladen und manuell auf LinkedIn hochladen (oder via
          Native-Scheduler / Taplio einplanen).
        </p>
      </div>
    </div>
  );
}

function ResultPreview({ result, label, compact, onDownload, onRegenerate, regenBusy }) {
  return (
    <div className={`${compact ? "mt-1" : "mt-5"} space-y-2`}>
      <div className="rounded-xl overflow-hidden border border-cream-300 bg-cream-50">
        <img
          src={result.url}
          alt={label || "Generiertes Bild"}
          className="w-full h-auto"
          loading="lazy"
        />
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onDownload}
          className="rounded-full border-cream-300 hover:bg-cream-200 flex-1"
        >
          <Download className="h-3.5 w-3.5 mr-1.5"/> Download
        </Button>
        {onRegenerate && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerate}
            disabled={regenBusy}
            className="rounded-full border-cream-300 hover:bg-cream-200"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${regenBusy ? "animate-spin" : ""}`}/>
          </Button>
        )}
      </div>
    </div>
  );
}
